import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { decodeHtmlEntities } from '@/common/utils';
import { useCurrentUserProfile } from '../hook/useCurrentUserProfile';
import { CarouselPreviewStrip } from './CarouselPreviewStrip';
import { renderWhatsAppText } from './renderWhatsAppText';
import { splitDynamicPreview } from '../utils/dynamicContactVars';

function normalizeCarouselCards(template) {
  if (Array.isArray(template?.cards) && template.cards.length) {
    return template.cards.map((card, index) => ({
      id: card.id || `card-${index}`,
      header_type: (card.header_type || 'IMAGE').toUpperCase(),
      image_base64: card.image_base64 || '',
      video_base64: card.video_base64 || '',
      previewUrl: card.previewUrl || null,
      body_text: card.body_text || '',
      buttons: (card.buttons || []).map((btn, btnIndex) => ({
        id: btn.id || `btn-${index}-${btnIndex}`,
        type:
          btn.type === 'URL' || btn.type === 'url'
            ? 'url'
            : btn.type === 'PHONE_NUMBER' || btn.type === 'phone'
              ? 'phone'
              : 'quick_reply',
        text: btn.text || '',
        value: btn.value || btn.url || '',
      })),
    }));
  }

  const carouselComp = template?.components?.find((c) => c.type === 'CAROUSEL');
  if (carouselComp?.cards) {
    return carouselComp.cards.map((card, index) => {
      const components = card.components || [];
      const header = components.find((c) => c.type === 'HEADER');
      const body = components.find((c) => c.type === 'BODY');
      const buttonsComp = components.find((c) => c.type === 'BUTTONS');
      return {
        id: `card-${index}`,
        header_type: (header?.format || 'IMAGE').toUpperCase(),
        image_base64: '',
        video_base64: '',
        previewUrl: null,
        body_text: body?.text || '',
        buttons: (buttonsComp?.buttons || []).map((btn, btnIndex) => ({
          id: `btn-${index}-${btnIndex}`,
          type:
            btn.type === 'URL'
              ? 'url'
              : btn.type === 'PHONE_NUMBER'
                ? 'phone'
                : 'quick_reply',
          text: btn.text || '',
        })),
      };
    });
  }

  return [];
}

export const TemplatePhonePreview = ({
  template,
  templateValues,
  message,
  mediaFile,
  carouselMediaFiles = {},
  className = '',
}) => {
  const { companyName, avatarUrl } = useCurrentUserProfile();

  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null);
  const [imageEnlarged, setImageEnlarged] = useState(false);

  useEffect(() => {
    if (!imageEnlarged) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setImageEnlarged(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [imageEnlarged]);

  useEffect(() => {
    setImageEnlarged(false);
    if (!mediaFile) {
      setMediaPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(mediaFile);
    setMediaPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [mediaFile]);

  const isCarousel =
    (template?.template_type || '').toUpperCase() === 'CAROUSEL' ||
    (Array.isArray(template?.cards) && template.cards.length > 0) ||
    Boolean(template?.components?.some((c) => c.type === 'CAROUSEL'));

  const carouselCards = isCarousel ? normalizeCarouselCards(template) : [];

  const getTemplateComponents = (template) => {
    let headerType = 'None';
    let headerText = '';
    let bodyText = '';
    let footerText = '';
    let buttons = [];

    if (template && template.components && Array.isArray(template.components)) {
      template.components.forEach((comp) => {
        if (comp.type === 'HEADER') {
          const format = comp.format || 'TEXT';
          if (format === 'TEXT') {
            headerType = 'Text';
            headerText = comp.text || '';
          } else if (format === 'IMAGE') {
            headerType = 'Image';
          } else if (format === 'DOCUMENT') {
            headerType = 'Document';
          } else if (format === 'VIDEO') {
            headerType = 'Video';
          }
        } else if (comp.type === 'BODY') {
          bodyText = comp.text || '';
        } else if (comp.type === 'FOOTER') {
          footerText = comp.text || '';
        } else if (comp.type === 'BUTTONS') {
          if (comp.buttons && Array.isArray(comp.buttons)) {
            buttons = comp.buttons.map((btn) => ({
              id: btn.id || Math.random(),
              type:
                btn.type === 'URL'
                  ? 'url'
                  : btn.type === 'PHONE_NUMBER'
                    ? 'phone'
                    : 'quick_reply',
              text: btn.text || '',
            }));
          }
        }
      });
    }

    return { headerType, headerText, bodyText, footerText, buttons };
  };

  const renderPreview = (text, type = 'BODY', cardIndex = null) => {
    if (!text) return null;
    return renderWhatsAppText(decodeHtmlEntities(text), (id) => {
      let varId = `body_var_${id}`;
      if (type === 'HEADER') varId = `header_var_${id}`;
      else if (type === 'CARD_BODY' && cardIndex !== null) {
        varId = `card_${cardIndex}_body_var_${id}`;
      }
      const val = templateValues?.[varId];
      if (val) {
        return splitDynamicPreview(val).map((part, index) =>
          part.type === 'field' ? (
            <span
              key={`${varId}-${index}`}
              className="inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded-full bg-[#25D366]/20 border border-[#25D366]/40 text-[11px] font-semibold text-[#075E54]"
            >
              {part.field.sample}
            </span>
          ) : (
            <span key={`${varId}-${index}`} className="font-bold text-black">
              {part.text}
            </span>
          ),
        );
      }
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#25D366]/20 border border-[#25D366]/30 text-xxs font-mono font-bold text-[#075E54] mx-0.5">
          {id}
        </span>
      );
    });
  };

  const previewDetails = getTemplateComponents(template);
  const carouselBody = template?.body_text || previewDetails.bodyText;
  const bodyNodes = template
    ? renderPreview(isCarousel ? carouselBody : previewDetails.bodyText, 'BODY')
    : message && typeof message === 'string' && message.trim()
      ? renderWhatsAppText(message)
      : null;

  return (
    <div
      className={`w-[290px] sm:w-[320px] xl:w-[340px] max-w-full h-[520px] sm:h-[640px] min-h-0 rounded-[34px] border-[8px] border-phone-bezel bg-white relative shadow-xl flex flex-col overflow-hidden select-none border-t-[10px] border-b-[10px] mx-auto ${className}`}
    >
      {/* Notch */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-4 bg-phone-bezel rounded-b-[13px] z-50 flex items-center justify-center pointer-events-none">
        <div className="w-6 h-0.5 bg-[#1a1a1a] rounded-full" />
      </div>

      {/* Mobile Screen Area */}
      <div
        className="w-full h-full min-h-0 grid overflow-hidden bg-[#F7F8FA]"
        style={{ gridTemplateRows: 'auto auto minmax(0, 1fr) auto' }}
      >
        {/* Status Bar */}
        <div className="h-5.5 w-full flex items-center justify-between px-3.5 text-[10px] font-bold text-[#111B21] z-40 bg-white select-none pt-0.5 shrink-0">
          <span>09:41</span>
          <div className="flex items-center gap-1">
            <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24">
              <path d="M2 22h20V2z" />
            </svg>
            <div className="w-3.5 h-2 border border-[#111B21]/60 rounded-sm flex items-center p-0.5 relative">
              <div className="h-full w-3/4 bg-[#111B21]/80 rounded-[1px]" />
            </div>
          </div>
        </div>

        {/* WhatsApp Header */}
        <div className="bg-white px-2.5 py-2 flex items-center justify-between text-[#111B21] border-b border-[#E5E5E5] z-30 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <svg
              className="w-3.5 h-3.5 text-[#111B21] shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>

            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="w-7 h-7 rounded-full object-cover border border-[#0088FF]/20 shrink-0"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-[#0088FF]/15 border border-[#0088FF]/20 flex items-center justify-center font-bold text-xs text-[#0088FF] shrink-0">
                {companyName.charAt(0).toUpperCase()}
              </div>
            )}

            <div className="text-left flex flex-col justify-center min-w-0">
              <div className="flex items-center gap-1">
                <span className="text-xs font-bold leading-none truncate max-w-[130px]">
                  {companyName}
                </span>
                <svg className="w-3 h-3 shrink-0" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="#53bdeb" />
                  <path
                    d="M9 12.5l2 2 4-4.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </div>
              <span className="text-[10px] text-[#8696A0] leading-none mt-0.5">
                Online
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0 text-[#111B21]">
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 10l4.55-2.27A1 1 0 0121 8.62v6.76a1 1 0 01-1.45.89L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 5a2 2 0 012-2h3l2 5-2.5 1.5a11 11 0 005 5L14 14l5 2v3a2 2 0 01-2 2h-1C8.61 21 3 15.39 3 8V5z"
              />
            </svg>
          </div>
        </div>

        {/* Mock Chat Window Feed Area */}
        <div className="min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain custom-scrollbar flex flex-col p-3 bg-[#F7F8FA] justify-start gap-1.5">
          {/* Encryption banner */}
          <div className="self-stretch bg-[#FEF6D8] text-[#7A6A2E] text-micro leading-tight text-center py-1 px-2 rounded-md">
            Messages to this chat and calls are now secured with end-to-end
            encryption. Tap to learn more.
          </div>

          <div className="self-center bg-white border border-[#E5E5E5] text-[#8696A0] text-micro font-bold uppercase py-0.5 px-2.5 rounded">
            Today
          </div>

          <div className="flex flex-col items-start gap-1.5 w-full min-w-0 text-left">
            {isCarousel ? (
              <>
                <div className="bg-white text-[#111B21] rounded-xl rounded-tl-none shadow-sm max-w-[92%] min-w-0 w-fit border border-[#E5E5E5] overflow-hidden">
                  <div className="p-2.5 flex flex-col">
                    <span className="text-xs text-[#111B21] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:break-word] block max-w-full">
                      {bodyNodes || (
                        <span className="text-[#8696A0] italic">
                          Your message preview...
                        </span>
                      )}
                    </span>
                    <div className="flex items-center justify-end gap-1 mt-1 self-end">
                      <span className="text-[10px] text-[#8696A0] font-medium">
                        16:04
                      </span>
                    </div>
                  </div>
                </div>
                <CarouselPreviewStrip
                  cards={carouselCards}
                  parseBody={(text, index) =>
                    renderPreview(text, 'CARD_BODY', index)
                  }
                  compact
                  carouselMediaFiles={carouselMediaFiles}
                />
              </>
            ) : (
              <>
                {/* Bubble + buttons card */}
                <div
                  className={`bg-white text-[#111B21] rounded-xl rounded-tl-none shadow-sm max-w-[92%] min-w-0 border border-[#E5E5E5] divide-y divide-[#E5E5E5] overflow-hidden ${
                    previewDetails.headerType === 'Image' && mediaPreviewUrl
                      ? 'w-full'
                      : 'w-fit'
                  }`}
                >
                  <div className="p-2.5 flex flex-col">
                    {/* Mockup Header Type */}
                    {previewDetails.headerType !== 'None' && (
                      <div className="mb-1.5 pb-1.5 border-b border-[#E5E5E5] text-xs leading-tight text-[#111B21] flex flex-col gap-1 w-full">
                        {previewDetails.headerType === 'Text' && (
                          <span className="font-bold text-[#111B21] tracking-wide block truncate">
                            {renderPreview(previewDetails.headerText, 'HEADER')}
                          </span>
                        )}

                        {previewDetails.headerType === 'Image' &&
                          (mediaPreviewUrl ? (
                            <button
                              type="button"
                              className="block w-full p-0 border-0 bg-[#F0F2F5] cursor-zoom-in rounded-md overflow-hidden [container-type:inline-size]"
                              onClick={() => setImageEnlarged(true)}
                              aria-label="Enlarge selected image"
                            >
                              <img
                                src={mediaPreviewUrl}
                                alt="Selected template image"
                                className="block w-full h-auto max-h-[100cqw] object-contain object-center bg-[#F0F2F5]"
                              />
                            </button>
                          ) : (
                            <div className="w-full h-16 rounded-md bg-[#F0F2F5] border border-[#E5E5E5] flex items-center justify-center">
                              <svg
                                className="w-5 h-5 text-[#8696A0]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <rect
                                  width="18"
                                  height="18"
                                  x="3"
                                  y="3"
                                  rx="2"
                                  strokeWidth="2"
                                />
                                <circle cx="9" cy="9" r="2" strokeWidth="2" />
                                <path
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"
                                />
                              </svg>
                            </div>
                          ))}

                        {previewDetails.headerType === 'Document' && (
                          <div className="w-full h-8 rounded-md bg-[#F0F2F5] border border-[#E5E5E5] flex items-center gap-1.5 px-2 text-[#111B21]">
                            <svg
                              className="w-4 h-4 text-red-500 shrink-0"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <span className="text-[11px] font-bold truncate">
                              Document_Preview.pdf
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Body Text */}
                    <span className="text-xs text-[#111B21] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:break-word] block max-w-full">
                      {bodyNodes || (
                        <span className="text-[#8696A0] italic">
                          Your message preview...
                        </span>
                      )}
                    </span>

                    {/* Footer */}
                    {previewDetails.footerText && (
                      <span className="text-[10px] text-[#8696A0] mt-1.5 block leading-tight break-words">
                        {previewDetails.footerText}
                      </span>
                    )}

                    {/* Stamp */}
                    <div className="flex items-center justify-end gap-1 mt-1 self-end">
                      <span className="text-[10px] text-[#8696A0] font-medium">
                        16:04
                      </span>
                      <svg
                        className="w-3 h-3 text-[#53BDEB] fill-current"
                        viewBox="0 0 24 24"
                      >
                        <path d="M0 0h24v24H0z" fill="none" />
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </div>
                  </div>

                  {/* Buttons mockup */}
                  {previewDetails.buttons.length > 0 &&
                    previewDetails.buttons.map((btn) => (
                      <button
                        key={btn.id}
                        type="button"
                        className="w-full h-8 flex items-center justify-center gap-1.5 text-xs text-[#0088FF] font-semibold select-none hover:bg-black/[0.02] transition-colors"
                      >
                        <span>{btn.text || 'Button text...'}</span>
                      </button>
                    ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bottom input bar */}
        <div className="bg-white p-2 flex items-center gap-2 text-[#8696A0] border-t border-[#E5E5E5] z-30 shrink-0">
          <div className="flex-grow h-7 px-3 rounded-full bg-[#F0F2F5] flex items-center text-xs">
            <span className="text-[#8696A0]">Message</span>
          </div>
          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white shrink-0">
            <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
            </svg>
          </div>
        </div>
      </div>
      {imageEnlarged &&
        mediaPreviewUrl &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
            onClick={() => setImageEnlarged(false)}
            role="presentation"
          >
            <img
              src={mediaPreviewUrl}
              alt="Selected template image"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};
